import { registerPlugin } from "@capacitor/core";

/**
 * 车辆属性值数据结构
 */
export interface CarPropertyClientValue {
  /** 属性名称，格式为 "类::属性名"，例如 "CAN::VehSpd" 或 "SOA::INSInfo_MSG_InsLatitude" */
  propertyName: string;
  /** 属性值，可以是数字、字符串、布尔值或数组等 */
  value: any;
  /** 状态枚举值：OK, TRY_AGAIN, INVALID_ARG, NOT_AVAILABLE, ACCESS_DENIED, INTERNAL_ERROR */
  status: string;
  /** 时间戳（毫秒） */
  timestamp: number;
  /** 事件类型：update（属性更新）、set（属性设置成功）、setError（属性设置失败） */
  type?: "update" | "set" | "setError";
}
/**
 * 订阅选项标志（可组合使用，如 SubscribeOptionFlags.UPDATE | SubscribeOptionFlags.SET）
 */
export enum SubscribeOptionFlags {
  /** 属性值有更新（value 可能不变，仅时间戳或状态改变，收到底层信号或 set 调用均会触发） */
  UPDATE = 1,
  /** 有应用使用 set 接口设置属性值 */
  SET = 2,
  /** 有应用使用 set 接口设置属性值时失败 */
  SET_ERROR = 4,
  /** 与 UPDATE 配合，仅 value 改变时通知 */
  ON_CHANGE = 8,
  /** 仅变化（暂时不用） */
  ONLY_WHEN_CHANGE = 16,
  /** 是否在订阅时需要立即回调当前 value */
  NEED_CURRENT_VALUE = 32,
}

/**
 * CarPropertyClient 插件接口定义
 */
export interface CarPropertyClientPlugin {
  getProperty(options: {
    propertyName?: string;
    propertyNames?: string[];
  }): Promise<CarPropertyClientValue | { properties: CarPropertyClientValue[] }>;

  setProperty(options: {
    propertyName?: string;
    propertyNames?: string[];
    value?: any;
    values?: any[];
    valueType?: "float" | "int" | "boolean" | "string";
  }): Promise<
    | { propertyName: string; success: boolean; status?: string }
    | {
        results: Array<{
          propertyName: string;
          success: boolean;
          status?: string;
          error?: string;
        }>;
      }
  >;

  subscribeProperty(options: {
    propertyName?: string;
    propertyNames?: string[];
    updateRateMs?: number;
    eventFlags?: SubscribeOptionFlags;
  }): Promise<
    | { propertyName: string; eventName: string }
    | { propertyNames: string[]; eventNames: string[] }
  >;

  unsubscribeProperty(options: {
    propertyName?: string;
    propertyNames?: string[];
  }): Promise<void>;

  removeAllListeners(): Promise<void>;

  addListener(
    eventName: string,
    listenerFunc: (data: CarPropertyClientValue) => void
  ): Promise<{ remove: () => void }>;

  isConnected(): Promise<{ connected: boolean }>;
}



const CarPropertyClient = registerPlugin<CarPropertyClientPlugin>(
  "CarPropertyClient"
);

class CarPropertyClientClass {
  private eventListeners = new Map<
    string,
    { remove: () => Promise<void> | void }
  >();

  async subscribeProperty(
    options: {
      propertyName?: string;
      propertyNames?: string[];
      updateRateMs?: number;
      eventFlags?: SubscribeOptionFlags;
    },
    callback?: (data: CarPropertyClientValue) => void
  ): Promise<void> {
    const propertyNames: string[] =
      options.propertyNames ||
      (options.propertyName ? [options.propertyName] : []);

    if (propertyNames.length === 0) {
      throw new Error("必须提供 propertyName 或 propertyNames");
    }

    if (callback) {
      for (const propertyName of propertyNames) {
        const existingListener = this.eventListeners.get(propertyName);
        if (existingListener) {
          await existingListener.remove();
        }
        const listener = await CarPropertyClient.addListener(
          propertyName,
          callback
        );
        this.eventListeners.set(propertyName, listener);
      }
    }

    await CarPropertyClient.subscribeProperty(options);
  }

  async unsubscribeProperty(options: {
    propertyName?: string;
    propertyNames?: string[];
  }): Promise<void> {
    const propertyNames: string[] =
      options.propertyNames ||
      (options.propertyName ? [options.propertyName] : []);

    for (const propertyName of propertyNames) {
      const listener = this.eventListeners.get(propertyName);
      if (listener) {
        await listener.remove();
        this.eventListeners.delete(propertyName);
      }
    }

    await CarPropertyClient.unsubscribeProperty(options);
  }

  async removeAllListeners(): Promise<void> {
    for (const [, listener] of this.eventListeners) {
      await listener.remove();
    }
    this.eventListeners.clear();
    await CarPropertyClient.removeAllListeners();
  }

  async getProperty(options: {
    propertyName?: string;
    propertyNames?: string[];
  }): Promise<
    CarPropertyClientValue | { properties: CarPropertyClientValue[] }
  > {
    return CarPropertyClient.getProperty(options);
  }

  async setProperty(options: {
    propertyName?: string;
    propertyNames?: string[];
    value?: any;
    values?: any[];
    valueType?: "float" | "int" | "boolean" | "string";
  }): Promise<
    | { propertyName: string; success: boolean; status?: string }
    | {
        results: Array<{
          propertyName: string;
          success: boolean;
          status?: string;
          error?: string;
        }>;
      }
  > {
    return CarPropertyClient.setProperty(options);
  }

  async isConnected(): Promise<{ connected: boolean }> {
    return CarPropertyClient.isConnected();
  }
}

export const CarPropertyClientClient = new CarPropertyClientClass();
export default CarPropertyClientClient;
